import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { faClose, faMapLocationDot } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { useCreateCompany } from "../../../hooks/Company/useCreateCompany"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { AddressForm } from "../../../components/Address/AddressForm"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface CompanyAddressPupopProps {
  onClose: () => void
}

export const CompanyAddressPupop: React.FC<CompanyAddressPupopProps> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const { handleSaveCompany } = useSaveCompany()

  const { handleGetCompany } = useGetCompany()

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const addressFormRef = useRef<any>(null)

  const { setAddressEntity, handleInitialValue, handleSubmitForm, addressEntity: pickupAddress } = useCreateCompany()

  useEffect(() => {
    handleInitialValue(companyMemory)
  }, [])

  const handleTrySave = () => {
    const validatedAddress = addressFormRef.current?.validate() ?? null

    if (!validatedAddress) {
      return
    }

    setAddressEntity(validatedAddress)

    const data = handleSubmitForm()

    data.addressEntity = validatedAddress

    handleSaveCompany(data, () => {
      handleGetCompany(() => handleInitialValue(companyMemory))
      onClose()
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("label_title_company_address")}
              icon={faMapLocationDot}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.myrestaurant.mycompany.companyaddresspupop.1"
                    variant="global"
                    title={t("helpPopup:global.pickupAddress.title")}
                    description={t("helpPopup:global.pickupAddress.description")}
                    bullets={t("helpPopup:global.pickupAddress.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyAddressPupop.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()

                handleTrySave()
              }}
            >
              <AddressForm ref={addressFormRef} addressEntity={pickupAddress ?? undefined} disable={canEdite} />

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCompany}>
                  <Button
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyAddressPupop.button.save"
                  />
                </CanView>

                <GetSpace right={5} />

                <Button
                  label={t("buttons:button_register")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGRegisterColor}
                  onClick={handleEnableRegister}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyAddressPupop.button.register"
                />
              </section>
            </form>

            {isRegisterEnable && (
              <ViewRegister id={companyMemory.id} typeEntity="CompanyEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
